import React, { useState, useEffect, useCallback } from 'react';
import { ref, onValue, get } from 'firebase/database';
import { db } from '../../../../integrations/firebase';
import { Pin, PinOff, X, ChevronRight } from 'lucide-react';
import { useToast } from '../../../../shared/ui/Toast';
import { MessageService } from '../../services/MessageService';

function PinnedMessagesModal({ chatId, onScrollToMessage, canUnpin, onClose }) {
  const [pins, setPins] = useState([]);
  const [loading, setLoading] = useState(true);
  const { showToast } = useToast();

  const fetchPins = useCallback(async () => {
    setLoading(true);
    try {
      const snap = await get(ref(db, `conversations/${chatId}/pinnedMessages`));
      if (snap.exists()) {
        const data = snap.val();
        const list = Object.entries(data).map(([messageId, pin]) => ({
          messageId,
          ...pin,
        }));
        list.sort((a, b) => (b.pinnedAt || 0) - (a.pinnedAt || 0));

        // Resolve pinnedBy UIDs and senderName UIDs to real names
        const uidSet = new Set();
        list.forEach(pin => {
          if (pin.pinnedBy && pin.pinnedBy.length > 15) uidSet.add(pin.pinnedBy);
          if (pin.senderName && pin.senderName.length > 15) uidSet.add(pin.senderName);
          if (pin.senderId) uidSet.add(pin.senderId);
        });

        const nameMap = {};
        await Promise.all([...uidSet].map(async (uid) => {
          try {
            const uSnap = await get(ref(db, `users/${uid}/name`));
            if (uSnap.exists()) nameMap[uid] = uSnap.val();
            else {
              const dSnap = await get(ref(db, `users/${uid}/displayName`));
              if (dSnap.exists()) nameMap[uid] = dSnap.val();
            }
          } catch(e) {}
        }));

        // Replace UIDs with resolved names
        list.forEach(pin => {
          if (pin.pinnedBy && nameMap[pin.pinnedBy]) pin.pinnedBy = nameMap[pin.pinnedBy];
          if (pin.senderName && nameMap[pin.senderName]) pin.senderName = nameMap[pin.senderName];
          if (!pin.senderName && pin.senderId && nameMap[pin.senderId]) pin.senderName = nameMap[pin.senderId];
        });

        setPins(list);
      } else {
        setPins([]);
      }
    } catch (err) {
      showToast('Failed to load pinned messages', 'error');
    } finally {
      setLoading(false);
    }
  }, [chatId, showToast]);

  useEffect(() => {
    fetchPins();
  }, [fetchPins]);

  const handleUnpin = async (e, messageId) => {
    e.stopPropagation();
    try {
      await MessageService.unpinMessage(chatId, messageId);
      showToast('Message unpinned', 'success');
      fetchPins();
    } catch (err) {
      showToast('Failed to unpin message', 'error');
    }
  };

  const handleSelect = (messageId) => {
    onScrollToMessage(messageId);
    onClose();
  };

  return (
    <div
      className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl w-full max-w-lg shadow-xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <Pin size={18} className="text-amber-500" />
            <h3 className="text-base font-semibold text-gray-800">Pinned Messages</h3>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto max-h-[60vh] px-5 py-3">
          {loading ? (
            <div className="flex items-center justify-center py-10 text-sm text-gray-400">
              Loading…
            </div>
          ) : pins.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 text-sm text-gray-400">
              <PinOff size={28} className="mb-2 text-gray-300" />
              No pinned messages
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              {pins.map((pin) => (
                <div
                  key={pin.messageId}
                  onClick={() => handleSelect(pin.messageId)}
                  className="group flex items-start gap-3 p-3 rounded-xl hover:bg-amber-50 cursor-pointer transition-colors border border-transparent hover:border-amber-100"
                >
                  <Pin size={14} className="text-amber-400 mt-1 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-800 line-clamp-2 leading-snug">
                      {pin.text
                        ? pin.text.length > 120
                          ? pin.text.slice(0, 120) + '…'
                          : pin.text
                        : 'Attachment'}
                    </p>
                    <div className="flex items-center gap-2 mt-1.5 text-xs text-gray-400">
                      {pin.senderName && (
                        <span className="font-medium text-gray-500">{pin.senderName}</span>
                      )}
                      {pin.pinnedBy && (
                        <>
                          <span>·</span>
                          <span>Pinned by {pin.pinnedBy}</span>
                        </>
                      )}
                      {pin.pinnedAt && (
                        <>
                          <span>·</span>
                          <span>{new Date(pin.pinnedAt).toLocaleDateString()}</span>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0 mt-1">
                    {canUnpin && (
                      <button
                        onClick={(e) => handleUnpin(e, pin.messageId)}
                        className="p-1.5 rounded-lg text-gray-300 hover:text-red-500 hover:bg-red-50 transition-colors opacity-0 group-hover:opacity-100"
                        title="Unpin"
                      >
                        <PinOff size={14} />
                      </button>
                    )}
                    <ChevronRight size={14} className="text-gray-300 group-hover:text-amber-400 transition-colors" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function PinnedMessageBar({ chatId, onScrollToMessage, canUnpin }) {
  const [latestPin, setLatestPin] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [showAllPins, setShowAllPins] = useState(false);

  useEffect(() => {
    if (!chatId) return;

    setDismissed(false);

    const pinRef = ref(db, `conversations/${chatId}/latestPinnedMessage`);
    const unsub = onValue(pinRef, async (snap) => {
      if (snap.exists()) {
        const pin = { ...snap.val() };
        // Resolve senderName if it looks like a UID (length > 15)
        const resolveUid = async (uid) => {
          try {
            const uSnap = await get(ref(db, `users/${uid}/name`));
            if (uSnap.exists()) return uSnap.val();
            const dSnap = await get(ref(db, `users/${uid}/displayName`));
            if (dSnap.exists()) return dSnap.val();
          } catch(e) {}
          return null;
        };
        if (pin.senderName && pin.senderName.length > 15) {
          const name = await resolveUid(pin.senderName);
          if (name) pin.senderName = name;
        }
        if (!pin.senderName && pin.senderId) {
          const name = await resolveUid(pin.senderId);
          if (name) pin.senderName = name;
        }
        setLatestPin(pin);
      } else {
        setLatestPin(null);
      }
    });

    return () => unsub();
  }, [chatId]);

  if (!latestPin || dismissed) return null;

  const truncatedText = latestPin.text
    ? latestPin.text.length > 60
      ? latestPin.text.slice(0, 60) + '…'
      : latestPin.text
    : 'Attachment';

  return (
    <>
      <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 flex items-center gap-3 rounded-t-lg">
        <Pin size={15} className="text-amber-500 shrink-0" />

        <div
          className="flex-1 min-w-0 cursor-pointer flex items-center gap-2 truncate"
          onClick={() => onScrollToMessage(latestPin.messageId)}
        >
          {latestPin.senderName && (
            <span className="text-xs font-semibold text-gray-700 shrink-0">
              {latestPin.senderName}
            </span>
          )}
          <span className="text-xs text-gray-500 truncate">{truncatedText}</span>
        </div>

        <button
          onClick={() => setShowAllPins(true)}
          className="text-xs text-amber-600 hover:text-amber-800 font-medium whitespace-nowrap flex items-center gap-0.5 transition-colors"
        >
          View All
          <ChevronRight size={13} />
        </button>

        <button
          onClick={() => setDismissed(true)}
          className="p-1 rounded hover:bg-amber-100 text-amber-400 hover:text-amber-600 transition-colors"
        >
          <X size={14} />
        </button>
      </div>

      {showAllPins && (
        <PinnedMessagesModal
          chatId={chatId}
          onScrollToMessage={onScrollToMessage}
          canUnpin={canUnpin}
          onClose={() => setShowAllPins(false)}
        />
      )}
    </>
  );
}

export default PinnedMessageBar;
